import HitCard from "components/Cards/HitCard";
import { RootState } from "MyTypes";
import React, { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { areAllLatestHitsCommentsLoaded } from "state/selectors/searchSelectors";
import { HitStatus, LatestHitComment, NA } from "aderant-conflicts-models";
import { HitResult } from "./HitResult";
import { searchActions } from "state/actions";
import HitDetailMobilePage from "./HitDetailMobilePage";
import { CommentsContainer } from "components/HitComments/CommentsContainer";
import { getCurrentUser } from "state/selectors";
import { MOBILE_VIEWS } from "constants/mobileViews";
import { formatShortDateTimeDisplay } from "@aderant/aderant-react-components";

export interface HitsCardData {
    hitName: string;
    hitId: number;
    hitEntityId: string;
    hitStatus: HitStatus;
    hitCommentCount: number | null;
    commentedBy: string | null;
    commentedByUserId: string | null;
    latestCommentedOn: string | null;
    latestCommentText: string | null;
    hitType: string;
    client: string | null;
    matter: string | null;
    hasCommentError?: boolean;
}

interface SimpleUser {
    id: string;
    name: string;
}

interface HitCardsPageProps {
    hitResults: HitResult[] | undefined;
    hitComments: Record<string, LatestHitComment> | undefined;
    allUsers: SimpleUser[] | undefined;
    searchId?: string;
    toggleRequestTermsDialog: () => void;
    searchVersion?: any;
    userChangePermissions?: any;
    searchReadOnly: boolean;
    onHitChange?: (hitResult: HitResult) => (status?: HitStatus, hitOwnerId?: string | null) => void;
    hasReviewerRole?: boolean;
}

export function processHitResults(hitResults: HitResult[], hitComments: Record<string, LatestHitComment>, allUsers: SimpleUser[]): HitsCardData[] {
    return hitResults.map((result) => {
        const entityId = result.hit.sourceData.id;
        const commentData = hitComments[entityId];

        const baseHit: Omit<HitsCardData, "hitCommentCount" | "commentedBy" | "commentedByUserId" | "latestCommentedOn" | "latestCommentText" | "hasCommentError"> = {
            hitName: result.hit.sourceData.name,
            hitId: result.hit.id,
            hitEntityId: entityId,
            hitStatus: result.hitStatus,
            hitType: result.hit.sourceType || NA,
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            client: result["client"] as string || NA,
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            matter: result["matter"] as string || NA
        };

        if (!commentData) {
            return {
                ...baseHit,
                hitCommentCount: 0,
                commentedBy: null,
                commentedByUserId: null,
                latestCommentedOn: null,
                latestCommentText: null,
                hasCommentError: false
            };
        }
        if (commentData.isError) {
            return {
                ...baseHit,
                hitCommentCount: 0,
                commentedBy: null,
                commentedByUserId: null,
                latestCommentedOn: null,
                latestCommentText: null,
                hasCommentError: true
            };
        }
        const createdByUser = allUsers.find((u) => u.id === commentData.createdByUserId);
        return {
            ...baseHit,
            hitCommentCount: commentData.commentCount ?? 0,
            commentedBy: createdByUser ? createdByUser.name : commentData.createdByUserId,
            commentedByUserId: commentData.createdByUserId,
            latestCommentedOn: commentData.createdOn ? formatShortDateTimeDisplay(commentData.createdOn) : null,
            latestCommentText: commentData.latestComment || null,
            hasCommentError: false
        };
    });
}

const HitCardsPage = (props: HitCardsPageProps): JSX.Element => {
    const { hitResults, hitComments, allUsers, searchId, toggleRequestTermsDialog } = props;

    const dispatch = useDispatch();

    const mobileView = useSelector((state: RootState) => state.search.currentSearch.mobileView);
    const currentView = mobileView.currentView;
    const selectedHit = mobileView.selectedHit;
    const selectedHitResult = mobileView.selectedHitResult;

    const processedResults: HitsCardData[] | undefined = useMemo(() => {
        return (hitResults && hitComments && allUsers)
            ? processHitResults(hitResults, hitComments, allUsers)
            : undefined;
    }, [hitResults, hitComments, allUsers]);

    const isHitsCommentsLoaded: boolean = useSelector((state: RootState) => areAllLatestHitsCommentsLoaded(state, searchId ?? ""));
    const currentUser = useSelector(getCurrentUser);

    const scrollToTop = (): void => {
        const pageContainer = document.querySelector(".page");
        if (pageContainer) pageContainer.scrollTop = 0;
        else window.scrollTo({ top: 0, behavior: "auto" });
    };

    useEffect(() => {
        if (currentView === MOBILE_VIEWS.HIT_DETAIL) {
            scrollToTop();
        }
    }, [currentView]);

    const handleHitCardClick = (hit: HitsCardData) => {
        const hitResult = hitResults?.find((r) => r.hit.id === hit.hitId);
        if (!hitResult) return;
        if (props.searchVersion) {
            dispatch(searchActions.focusHit({
                searchId: props.searchVersion.searchId,
                versionId: props.searchVersion.id,
                hitIdentifier: {
                    requestTermId: hitResult.hitId.requestTermId,
                    hitEntityId: hitResult.hit.sourceData.id,
                    hitEntityType: hitResult.hit.sourceType
                }
            }));
        }
        if (hitResult.hit.sourceData.id && !hitResult.isRedacted) {
            dispatch(searchActions.fetchEntityDetails({
                entityType: hitResult.hit.sourceType,
                id: hitResult.hit.sourceData.id
            }));
        }
        dispatch(searchActions.setMobileSelectedHit({ hit, hitResult }));
        dispatch(searchActions.setMobileView(MOBILE_VIEWS.HIT_DETAIL));
    };

    const handleCommentClick = (hit: HitsCardData) => {
        const hitResult = hitResults?.find((r) => r.hit.sourceData.id === hit.hitEntityId);
        if (!hitResult) return;
        dispatch(searchActions.setMobileSelectedHit({ hit, hitResult }));
        dispatch(searchActions.setMobileView(MOBILE_VIEWS.COMMENTS));
    };

    // Render based on current view
    const findLatestForSelected = (): HitResult | null => {
        if (!selectedHitResult) return null;
        const refreshed = props.hitResults?.find((r) => r.hit.sourceData.id === selectedHitResult.hit.sourceData.id);
        return refreshed || selectedHitResult;
    };

    const renderCurrentView = () => {
        switch (currentView) {
            case MOBILE_VIEWS.HIT_DETAIL:
                const latestHitResult = findLatestForSelected();
                
                // Get the latest hit data from processedResults instead of using stale selectedHit
                const latestSelectedHit = selectedHit && processedResults ? 
                    processedResults.find(hit => hit.hitId === selectedHit.hitId) || selectedHit : selectedHit;
                
                return (
                    <div className="full-width">
                        <HitDetailMobilePage
                            data={latestHitResult}
                            hit={latestSelectedHit}
                            entityId={latestHitResult?.hit?.sourceData?.id || null}
                            searchVersion={props.searchVersion}
                            userChangePermissions={props.userChangePermissions}
                            searchReadOnly={props.searchReadOnly}
                            onHitChange={latestHitResult && props.onHitChange ? props.onHitChange(latestHitResult) : undefined}
                            toggleRequestTermsDialog={toggleRequestTermsDialog}
                            handleCommentClick={handleCommentClick}
                            hasReviewerRole={props.hasReviewerRole}
                        />
                    </div>
                );

            case MOBILE_VIEWS.COMMENTS:
                const latestHitResultForComments = findLatestForSelected();

                return (
                    <div className="full-width">
                        {selectedHit && latestHitResultForComments && currentUser && (
                            <div>
                                <CommentsContainer
                                    currentUser={currentUser}
                                    durableHitIdentifier={{
                                        hitEntityId: latestHitResultForComments.hit.sourceData.id ?? "",
                                        hitEntityType: latestHitResultForComments.hit.sourceType ?? "",
                                        requestTermId: latestHitResultForComments.hitId.requestTermId ?? ""
                                    }}
                                    searchVersionIdentifier={{
                                        searchId: props.searchVersion?.searchId ?? "",
                                        versionId: props.searchVersion?.id ?? ""
                                    }}
                                />
                            </div>
                        )}
                    </div>
                );

            case MOBILE_VIEWS.CARDS:
            default:
                if (!processedResults || processedResults.length === 0) return <div>No hits found</div>;
                const sortedHits = [...processedResults].sort((a, b) => a.hitName.localeCompare(b.hitName));
                return (
                    <>
                        {sortedHits.map((hit) => (
                            <HitCard
                                key={hit.hitEntityId}
                                hit={hit}
                                isHitsCommentsLoaded={isHitsCommentsLoaded}
                                toggleRequestTermsDialog={toggleRequestTermsDialog}
                                searchVersion={props.searchVersion}
                                onHitClick={handleHitCardClick}
                                searchReadOnly={props.searchReadOnly}
                                userChangePermissions={props.userChangePermissions}
                                onStatusChange={(() => {
                                    if (!props.onHitChange || !hitResults) return undefined;
                                    const foundHitResult = hitResults.find((r) => r.hit.id === hit.hitId);
                                    return foundHitResult ? props.onHitChange(foundHitResult) : undefined;
                                })()}
                                hasReviewerRole={props.hasReviewerRole}
                            />
                        ))}
                    </>
                );
        }
    };
    return <>{renderCurrentView()}</>;
};

export default HitCardsPage;
